import { type ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { LoadingIconComponent } from '@frontendlib/components/components/icon/loading-icon.component';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { type StitchFiltersHistoryRecord } from '@modules/common/services/history.service';
import { SortOrder } from '@modules/common/types/sort-order';
import { type Stitch } from '@modules/common/types/stitch';
import { ConnectionComponent } from '@modules/connections/components/connection/connection.component';
import { ConnectionsService } from '@modules/connections/services/connections.service';
import { type Connection } from '@modules/connections/types/connection';
import { KpKeywordChipComponent } from '@modules/knowledge/components/kp-keyword-chip/kp-keyword-chip.component';
import { type KpDeepModes, KnowledgePanelService } from '@modules/knowledge/services/knowledge-panel.service';
import { type SelectedMarker } from '@modules/knowledge/types/selected-marker';
import { type NeighborMarkers, resolveNeighborMarkers } from '@modules/knowledge/utils/resolve-neighbor-markers';
import { KnotComponent } from '@modules/knots/components/knot/knot.component';
import { KnotsService } from '@modules/knots/services/knots.service';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { type Knot } from '@modules/knots/types/knot';
import { TagComponent } from '@modules/tags/components/tag/tag.component';
import { TagsService } from '@modules/tags/services/tags.service';
import { type Tag } from '@modules/tags/types/tag';
import { NEIGHBORS_NAME_QUERY_MAX_LENGTH } from '@sharedlib/dtos/dto/knowledge/neighbors-search-query.dto';
import { combineLatest, forkJoin, type Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, scan, startWith, switchMap } from 'rxjs/operators';

export const KP_SEARCH_DEBOUNCE_MS = 250;
export const KP_SEARCH_LIMIT = 20;

export interface KpSearchMatches {
  readonly status: 'idle' | 'loading' | 'done';
  readonly knots: Knot[];
  readonly tags: Tag[];
  readonly connections: Connection[];
}

const IDLE: KpSearchMatches = { status: 'idle', knots: [], tags: [], connections: [] };
const LOADING: KpSearchMatches = { ...IDLE, status: 'loading' };
const NOTHING: KpSearchMatches = { ...IDLE, status: 'done' };

let nextMenuId = 0;

const MENU_STEPS: Readonly<Partial<Record<string, 1 | -1>>> = {
  ArrowDown: 1,
  ArrowRight: 1,
  ArrowUp: -1,
  ArrowLeft: -1,
};

function notSelected<T extends { name: string }>(items: T[], selected: { name: string }[]): T[] {
  return items.filter((item) => !selected.some((other) => other.name === item.name));
}

/**
 * The knowledge panel's search: a toggle beside the projected tab bar, a field below it and a dropdown of
 * the related knots, tags and connections whose name matches. A pick joins the selection; Enter in the
 * field adds the text as a keyword.
 */
@Component({
  selector: 'app-kp-search',
  templateUrl: './kp-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    OverlayModule,
    Button2Component,
    IconComponent,
    LoadingIconComponent,
    KnotComponent,
    TagComponent,
    ConnectionComponent,
    KpKeywordChipComponent,
  ],
  host: {
    class: 'block shrink-0',
  },
})
export class KpSearchComponent {
  private readonly kpService = inject(KnowledgePanelService);
  private readonly knotsService = inject(KnotsService);
  private readonly tagsService = inject(TagsService);
  private readonly connectionsService = inject(ConnectionsService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);
  private readonly splitView = inject(SplitViewService, { optional: true });

  /** Key of the `app-split-view` pane holding the search; it is expanded while the search is open. */
  public readonly splitKey = input<string | null>(null);

  private readonly field = viewChild<ElementRef<HTMLElement>>('field');
  private readonly input = viewChild<ElementRef<HTMLInputElement>>('input');
  private readonly menu = viewChild<ElementRef<HTMLElement>>('menu');
  private readonly toggleButton = viewChild<Button2Component>('toggleButton');

  protected readonly opened = signal(false);
  protected readonly text = signal('');
  protected readonly width = signal(0);
  protected readonly menuAttached = signal(false);

  private readonly contextItem = toSignal(this.kpService.getContextStitchItem(), { initialValue: null });
  protected readonly markers = toSignal(this.kpService.getSelectedMarkers(), { initialValue: [] });
  protected readonly disabled = computed(() => !this.contextItem() && !this.markers().length);

  protected readonly matches = toSignal(
    combineLatest([
      toObservable(this.opened),
      toObservable(this.text).pipe(
        map((text) => text.trim()),
        distinctUntilChanged(),
      ),
      this.kpService.getQueryFilter(),
      this.kpService.getContextStitchItem(),
      toObservable(this.kpService.getDeepModes()),
    ]).pipe(
      switchMap(([opened, text, selection, contextItem, deepModes]) =>
        opened && text
          ? timer(KP_SEARCH_DEBOUNCE_MS).pipe(
              switchMap(() => this.loadMatches(text, selection, contextItem, deepModes)),
              startWith(LOADING),
            )
          : of(IDLE),
      ),
      // The previous matches stay listed while the next ones load.
      scan((previous, next) => (next.status === 'loading' ? { ...previous, status: next.status } : next), IDLE),
    ),
    { initialValue: IDLE },
  );

  protected readonly searched = computed(() => this.matches().status === 'done');
  protected readonly loading = computed(() => this.matches().status === 'loading');
  protected readonly menuId = `kp-search-menu-${nextMenuId++}`;
  protected readonly queryMaxLength = NEIGHBORS_NAME_QUERY_MAX_LENGTH;

  protected readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
  ];

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.close();
      }
    });

    toObservable(this.splitKey)
      .pipe(
        switchMap((key) => (key && this.splitView ? this.splitView.getMinimized(key) : of(false))),
        distinctUntilChanged(),
        filter(Boolean),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.close());
  }

  protected toggle(): void {
    if (this.opened()) {
      this.close();
      return;
    }
    if (this.disabled()) {
      return;
    }

    this.opened.set(true);
    this.expandPane();
    afterNextRender(
      () => {
        this.width.set(Math.round(this.field()?.nativeElement.getBoundingClientRect().width ?? 0));
        // The pane may still be growing; a scroll-into-view would shift its content and detach the dropdown.
        this.input()?.nativeElement.focus({ preventScroll: true });
      },
      { injector: this.injector },
    );
  }

  /** Goes through the split view's own toggle, so a reopened pane gets its usual minimum size. */
  private expandPane(): void {
    const key = this.splitKey();
    if (key && this.splitView?.isMinimized(key)) {
      this.splitView.toggleClicked(key);
    }
  }

  protected close(): void {
    this.opened.set(false);
    this.text.set('');
  }

  protected handleInput(event: Event): void {
    if (event.target instanceof HTMLInputElement) {
      // `maxlength` does not bound a value set by script; the neighbors endpoints reject longer text.
      this.text.set(event.target.value.slice(0, NEIGHBORS_NAME_QUERY_MAX_LENGTH));
    }
  }

  protected clearText(): void {
    this.text.set('');
    this.input()?.nativeElement.focus();
  }

  protected addKeyword(event: Event): void {
    // Otherwise the browser turns this Enter into a click on the toggle, which takes focus below.
    event.preventDefault();
    const keyword = this.text().trim();
    if (!keyword) {
      return;
    }

    this.kpService.addKeyword(keyword);
    this.closeToToggle();
  }

  /** Clicks on the field, the toggle or the tabs are inside the host and keep the dropdown open. */
  protected handleOutsideClick(event: MouseEvent): void {
    if (event.target instanceof Node && this.host.nativeElement.contains(event.target)) {
      return;
    }
    this.close();
  }

  /** Escape inside the dropdown returns to the field; Escape elsewhere closes the search. */
  protected handleOverlayKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    const menu = this.menu()?.nativeElement;
    if (menu && document.activeElement instanceof Node && menu.contains(document.activeElement)) {
      event.preventDefault();
      this.input()?.nativeElement.focus();
      return;
    }
    this.closeToToggle();
  }

  /** The dropdown sits in an overlay at the end of the page, out of Tab's reach: ArrowDown leads into it. */
  protected focusMenu(event: Event): void {
    const [first] = this.menuOptions();
    if (first) {
      event.preventDefault();
      first.focus();
    }
  }

  protected handleMenuKeydown(event: KeyboardEvent): void {
    const step = MENU_STEPS[event.key];
    if (!step) {
      return;
    }

    const options = this.menuOptions();
    const index = options.findIndex((option) => option === document.activeElement);
    if (index === -1) {
      return;
    }

    event.preventDefault();
    const next = index + step;
    if (next < 0) {
      this.input()?.nativeElement.focus();
      return;
    }
    options[Math.min(next, options.length - 1)].focus();
  }

  protected pickKnot(knot: Knot): void {
    this.kpService.addKnotToSelection(knot, null, false);
    this.closeToToggle();
  }

  protected pickTag(tag: Tag): void {
    this.kpService.addTagToSelection(tag, null, false);
    this.closeToToggle();
  }

  protected pickConnection(connection: Connection): void {
    this.kpService.addConnectionToSelection(connection, null, false);
    this.closeToToggle();
  }

  /** The field goes away on close; focus lands on the toggle instead of the page body. */
  protected closeToToggle(): void {
    this.close();
    this.toggleButton()?.focus();
  }

  private menuOptions(): HTMLElement[] {
    return Array.from(this.menu()?.nativeElement.querySelectorAll<HTMLElement>('[data-menu-item]') ?? []);
  }

  protected removeMarker(marker: SelectedMarker): void {
    this.kpService.removeSelectedMarker(marker);
  }

  protected clearSelection(): void {
    this.kpService.clearSelection();
  }

  /** Mirrors the related boxes' query: same selection, open item and owner tenant, relevance first. */
  private loadMatches(
    text: string,
    selection: StitchFiltersHistoryRecord,
    contextItem: Stitch | null,
    deepModes: KpDeepModes,
  ): Observable<KpSearchMatches> {
    const { knots, tags, connections } = selection;
    if (!knots.length && !tags.length && !connections.length && !contextItem?.id) {
      return of(NOTHING);
    }

    const config = { requestContext: owningTenantContext(contextItem?.accountId) };
    const lookups = { knots: this.knotsService, tags: this.tagsService, connections: this.connectionsService };

    return resolveNeighborMarkers({ knots, tags, connections }, lookups, config).pipe(
      switchMap((markers) =>
        markers ? this.askNeighbors(text, markers, contextItem, deepModes, config) : of(NOTHING),
      ),
    );
  }

  /**
   * The three lists asked with `markers`, each carrying an id; a match already selected is left out by name.
   * A selected name that did not resolve has no row in this tenant, so no match can carry it.
   */
  private askNeighbors(
    text: string,
    markers: NeighborMarkers,
    contextItem: Stitch | null,
    deepModes: KpDeepModes,
    config: StitchRequestConfig,
  ): Observable<KpSearchMatches> {
    const { knots, tags, connections } = markers;
    const sortOrder: SortOrder = SortOrder.DESC;
    const filters = {
      ...markers,
      contextItems: contextItem?.id ? [contextItem.id] : [],
      query: text,
      limit: KP_SEARCH_LIMIT,
      sortBy: 'relevance' as const,
      sortOrder,
    };
    // Tag chips read `#name`; the stored name has no `#`.
    const tagQuery = text.replace(/^#/, '').trim();

    return forkJoin({
      knots: this.knotsService.getNeighbors({ ...filters, deepMode: deepModes.knots }, config).pipe(
        map((result) => result.knots),
        catchError(() => of<Knot[]>([])),
      ),
      tags: tagQuery
        ? this.tagsService.getNeighbors({ ...filters, query: tagQuery, deepMode: deepModes.tags }, config).pipe(
            map((result) => result.tags),
            catchError(() => of<Tag[]>([])),
          )
        : of<Tag[]>([]),
      connections: this.connectionsService.getNeighbors({ ...filters, deepMode: deepModes.connections }, config).pipe(
        map((result) => result.connections),
        catchError(() => of<Connection[]>([])),
      ),
    }).pipe(
      map((found) => ({
        status: 'done' as const,
        knots: notSelected(found.knots, knots),
        tags: notSelected(found.tags, tags),
        connections: notSelected(found.connections, connections),
      })),
    );
  }
}
